import React, { useState } from 'react';
import './HerbGallery.css';

interface Herb {
  id: number;
  commonName: string;
  latinName: string;
  activeIngredient: string;
  healthBenefits: string[];
  description: string;
  image: string;
  color: string;
}

interface HerbModalProps {
  herb: Herb | null;
  isOpen: boolean;
  onClose: () => void;
}

const HerbModal: React.FC<HerbModalProps> = ({ herb, isOpen, onClose }) => {
  if (!isOpen || !herb) return null;

  return (
    <div className="herb-modal-overlay" onClick={onClose}>
      <div className="herb-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        <div className="modal-content">
          <div className="modal-image">
            <div className={`herb-sketch ${herb.color}`}>
              {herb.image.endsWith('.jpg') || herb.image.endsWith('.png') || herb.image.endsWith('.jpeg') ? (
                <img src={herb.image} alt={herb.commonName} className="herb-modal-image" />
              ) : (
                herb.image
              )}
            </div>
          </div>
          <div className="modal-info">
            <h2>{herb.commonName}</h2>
            <h3 className="latin-name">{herb.latinName}</h3>
            
            <div className="active-ingredient">
              <h4>Active Ingredient:</h4>
              <p>{herb.activeIngredient}</p>
            </div>
            
            <div className="health-benefits">
              <h4>Health Benefits:</h4>
              <ul>
                {herb.healthBenefits.map((benefit, index) => (
                  <li key={index}>{benefit}</li>
                ))}
              </ul>
            </div>
            
            <div className="description">
              <h4>Traditional Use:</h4>
              <p>{herb.description}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HerbGallery: React.FC = () => {
  const [hoveredHerb, setHoveredHerb] = useState<Herb | null>(null);
  const [selectedHerb, setSelectedHerb] = useState<Herb | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const herbs: Herb[] = [
    {
      id: 1,
      commonName: "Echinacea",
      latinName: "Echinacea purpurea",
      activeIngredient: "Polysaccharides, Alkamides, Caffeic acid derivatives",
      healthBenefits: ["Immune system support", "Anti-inflammatory properties", "Wound healing", "Upper respiratory health"],
      description: "Native American traditional medicine herb known for its immune-boosting properties. Used for centuries to support the body's natural defense mechanisms.",
      image: "/herbs/echinacea.png",
      color: "purple"
    },
    {
      id: 2,
      commonName: "Turmeric",
      latinName: "Curcuma longa",
      activeIngredient: "Curcumin, Turmerone, Zingiberene",
      healthBenefits: ["Anti-inflammatory", "Antioxidant properties", "Joint health", "Digestive support", "Liver detoxification"],
      description: "Golden spice revered in Ayurvedic medicine for its powerful anti-inflammatory and healing properties. Essential for joint health and overall wellness.",
      image: "/herbs/turmeric.png",
      color: "golden"
    },
    {
      id: 3,
      commonName: "Lavender",
      latinName: "Lavandula angustifolia",
      activeIngredient: "Linalool, Linalyl acetate, Camphor",
      healthBenefits: ["Stress relief", "Sleep support", "Anxiety reduction", "Skin healing", "Antimicrobial properties"],
      description: "Calming herb traditionally used for relaxation and sleep. Its gentle properties make it perfect for stress management and skin care.",
      image: "/herbs/lavender.png",
      color: "lavender"
    },
    {
      id: 4,
      commonName: "Ginkgo",
      latinName: "Ginkgo biloba",
      activeIngredient: "Flavonoids, Terpenoids, Ginkgolides",
      healthBenefits: ["Cognitive function", "Memory enhancement", "Circulation support", "Antioxidant protection", "Age-related mental decline"],
      description: "Ancient tree species with leaves that support cognitive function and memory. Used traditionally to enhance mental clarity and circulation.",
      image: "/herbs/ginkgo.png",
      color: "green"
    },
    {
      id: 5,
      commonName: "Ginseng",
      latinName: "Panax ginseng",
      activeIngredient: "Ginsenosides, Polysaccharides, Peptides",
      healthBenefits: ["Energy enhancement", "Stress adaptation", "Immune support", "Mental performance", "Physical endurance"],
      description: "Renowned adaptogenic herb that helps the body manage stress while boosting energy and vitality. A cornerstone of traditional Chinese medicine.",
      image: "/herbs/ginseng.png",
      color: "beige"
    },
    {
      id: 6,
      commonName: "Chamomile",
      latinName: "Matricaria chamomilla",
      activeIngredient: "Apigenin, Bisabolol, Chamazulene",
      healthBenefits: ["Sleep support", "Digestive comfort", "Skin soothing", "Anxiety relief", "Anti-inflammatory"],
      description: "Gentle flower with calming properties, perfect for evening relaxation and digestive support. Safe and effective for all ages.",
      image: "/herbs/chamomile.png",
      color: "yellow"
    },
    {
      id: 7,
      commonName: "Milk Thistle",
      latinName: "Silybum marianum",
      activeIngredient: "Silymarin, Silybin, Silydianin",
      healthBenefits: ["Liver protection", "Detoxification support", "Antioxidant properties", "Hepatic regeneration", "Gallbladder health"],
      description: "Powerful liver-supporting herb with distinctive spiny leaves and purple flowers. Essential for liver detoxification and protection.",
      image: "/herbs/milk_thistle.png",
      color: "pink"
    },
    {
      id: 8,
      commonName: "Ashwagandha",
      latinName: "Withania somnifera",
      activeIngredient: "Withanolides, Alkaloids, Saponins",
      healthBenefits: ["Stress adaptation", "Cortisol regulation", "Energy balance", "Sleep quality", "Thyroid support"],
      description: "Ancient Ayurvedic adaptogen known as 'Indian Winter Cherry.' Helps the body adapt to stress while supporting energy and vitality.",
      image: "/herbs/ashwagandha.png",
      color: "orange"
    },
    {
      id: 9,
      commonName: "Holy Basil",
      latinName: "Ocimum tenuiflorum",
      activeIngredient: "Eugenol, Rosmarinic acid, Ursolic acid",
      healthBenefits: ["Stress relief", "Respiratory health", "Blood sugar support", "Immune function", "Mental clarity"],
      description: "Sacred herb in Ayurveda, revered for its ability to promote mental clarity and spiritual well-being while supporting physical health.",
      image: "/herbs/holy_basil.png",
      color: "green"
    },
    {
      id: 10,
      commonName: "Rhodiola",
      latinName: "Rhodiola rosea",
      activeIngredient: "Rosavin, Salidroside, Tyrosol",
      healthBenefits: ["Mental fatigue", "Physical endurance", "Stress adaptation", "Mood support", "Cognitive performance"],
      description: "Arctic adaptogen that thrives in harsh conditions, helping humans adapt to physical and mental stress while enhancing performance.",
      image: "/herbs/rhodiola.png",
      color: "rose"
    },
    {
      id: 11,
      commonName: "Elderberry",
      latinName: "Sambucus canadensis",
      activeIngredient: "Anthocyanins, Flavonoids, Vitamin C",
      healthBenefits: ["Immune support", "Antiviral properties", "Cold and flu relief", "Antioxidant protection", "Respiratory health"],
      description: "Dark purple berries packed with immune-supporting compounds. Traditional remedy for seasonal wellness and respiratory health.",
      image: "/herbs/elderberry.png",
      color: "dark-purple"
    },
    {
      id: 12,
      commonName: "Calendula",
      latinName: "Calendula officinalis",
      activeIngredient: "Triterpenes, Flavonoids, Carotenoids",
      healthBenefits: ["Wound healing", "Skin repair", "Anti-inflammatory", "Antimicrobial", "Digestive soothing"],
      description: "Bright orange flower known for its remarkable healing properties. Gentle yet effective for skin care and wound healing.",
      image: "/herbs/calendula.png",
      color: "orange"
    }
  ];

  const handleHerbClick = (herb: Herb) => {
    setSelectedHerb(herb);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedHerb(null);
  };

  return (
    <section id="herbs" className="herb-gallery">
      <div className="herb-gallery-container">
        <h2 className="section-title">Herb Library</h2>
        <h3 className="herb-gallery-subtitle">Natural Healing Through Traditional Medicine</h3>
        <p className="gallery-intro">
          For centuries, plants have been healing what modern medicine sometimes can't explain.
          Here's what I use—and why it works. Hover to see names, click to learn more about each herb.
        </p>
        
        <div className="herbs-grid">
          {herbs.map((herb) => (
            <div
              key={herb.id}
              className={`herb-card ${herb.color}`}
              onMouseEnter={() => setHoveredHerb(herb)}
              onMouseLeave={() => setHoveredHerb(null)}
              onClick={() => handleHerbClick(herb)}
            >
              <div className="herb-sketch">
                {herb.image.endsWith('.jpg') || herb.image.endsWith('.png') || herb.image.endsWith('.jpeg') ? (
                  <img src={herb.image} alt={herb.commonName} className="herb-image" />
                ) : (
                  herb.image
                )}
              </div>
              
              {hoveredHerb?.id === herb.id && (
                <div className="herb-tooltip">
                  <div className="herb-names">
                    <h4>{herb.commonName}</h4>
                    <p className="latin">{herb.latinName}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        
        <div className="gallery-note">
          <p>
            <strong>Note:</strong> These illustrations represent herbs I work with in my practice. 
            Always consult with a qualified healthcare provider before using medicinal herbs, 
            especially if you have existing health conditions or take medications.
          </p>
        </div>
      </div>
      
      <HerbModal 
        herb={selectedHerb} 
        isOpen={isModalOpen} 
        onClose={closeModal} 
      />
    </section>
  );
};

export default HerbGallery;
